import { STOMP_CONFIG } from './config';
import { DropReason, QueuedOutboundMessage } from './types';

export type OutboundDrop = {
  reason: DropReason;
  message: QueuedOutboundMessage;
};

export type OutboundQueueLimits = {
  maxCount: number;
  maxBytes: number;
  ttlMs: number;
};

const DEFAULT_LIMITS: OutboundQueueLimits = {
  maxCount: STOMP_CONFIG.outboundMaxQueuedCount,
  maxBytes: STOMP_CONFIG.outboundMaxQueuedBytes,
  ttlMs: STOMP_CONFIG.outboundTtlMs,
};

export class BoundedOutboundQueue {
  private items: QueuedOutboundMessage[] = [];
  private queuedBytes = 0;
  private limits: OutboundQueueLimits;

  constructor(limits: Partial<OutboundQueueLimits> = {}) {
    this.limits = { ...DEFAULT_LIMITS, ...limits };
  }

  get count() {
    return this.items.length;
  }

  get bytes() {
    return this.queuedBytes;
  }

  snapshot() {
    return { count: this.items.length, bytes: this.queuedBytes };
  }

  /**
   * Messages are appended in `enqueuedAt` order, so everything expired is a
   * prefix -- the common case (nothing expired) costs one comparison instead
   * of rebuilding the array and re-summing its bytes.
   */
  expire(now: number): OutboundDrop[] {
    if (
      !this.items.length ||
      now - this.items[0].enqueuedAt <= this.limits.ttlMs
    ) {
      // A fresh array, not a shared constant: `enqueue` appends its own
      // overflow drops to whatever this returns.
      return [];
    }

    let expired = 1;
    while (
      expired < this.items.length &&
      now - this.items[expired].enqueuedAt > this.limits.ttlMs
    ) {
      expired++;
    }

    const removed = this.items.splice(0, expired);
    const dropped: OutboundDrop[] = [];
    for (let i = 0; i < removed.length; i++) {
      this.queuedBytes -= removed[i].bytes;
      dropped.push({ reason: 'expired' as DropReason, message: removed[i] });
    }
    if (this.queuedBytes < 0) {
      this.queuedBytes = 0;
    }
    return dropped;
  }

  enqueue(message: QueuedOutboundMessage, now: number): OutboundDrop[] {
    const dropped = this.expire(now);

    while (
      this.items.length >= this.limits.maxCount ||
      this.queuedBytes + message.bytes > this.limits.maxBytes
    ) {
      const oldest = this.items.shift();
      if (!oldest) {
        break;
      }
      this.queuedBytes -= oldest.bytes;
      dropped.push({ reason: 'overflow', message: oldest });
    }

    this.items.push(message);
    this.queuedBytes += message.bytes;
    return dropped;
  }

  drain(maxCount: number): QueuedOutboundMessage[] {
    const batch = this.items.splice(0, maxCount);
    for (let i = 0; i < batch.length; i++) {
      this.queuedBytes -= batch[i].bytes;
    }
    if (this.queuedBytes < 0) {
      this.queuedBytes = 0;
    }
    return batch;
  }

  prepend(messages: QueuedOutboundMessage[]) {
    if (!messages.length) {
      return;
    }
    this.items = messages.concat(this.items);
    for (let i = 0; i < messages.length; i++) {
      this.queuedBytes += messages[i].bytes;
    }
  }

  clear(): OutboundDrop[] {
    const dropped = this.items.map(message => ({
      reason: 'cleared' as DropReason,
      message,
    }));
    this.items = [];
    this.queuedBytes = 0;
    return dropped;
  }
}
